/**
 * RecoveryPackageReviewPage - BCM Manager review of submitted recovery packages
 * ISO 22301 Clause 8.4 - Business Continuity Plans
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Eye,
  Clock,
  AlertTriangle,
  Target,
  Zap,
  Timer,
  Snowflake,
  Wrench,
  FileText,
  Server,
  Users,
  Database,
  GitBranch,
  Building2,
  ArrowRight,
  Send,
  Edit,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
} from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: Edit },
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700", icon: Clock },
  under_review: { label: "Under Review", color: "bg-amber-100 text-amber-700", icon: Eye },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
  revision_required: { label: "Revision Required", color: "bg-sky-100 text-sky-700", icon: AlertTriangle },
};

const STRATEGY_CONFIG = {
  hot_standby: { label: "Hot Standby", color: "bg-red-100 text-red-700", icon: Zap, rto: "0-4 hours" },
  warm_standby: { label: "Warm Standby", color: "bg-amber-100 text-amber-700", icon: Timer, rto: "4-24 hours" },
  cold_standby: { label: "Cold Standby", color: "bg-blue-100 text-blue-700", icon: Snowflake, rto: "24-72 hours" },
  manual_workaround: { label: "Manual Workaround", color: "bg-slate-100 text-slate-700", icon: Wrench, rto: "Varies" },
};

export default function RecoveryPackageReviewPage() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewAction, setReviewAction] = useState("");
  const [reviewComments, setReviewComments] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (packageId) {
        // Load specific package
        const response = await api.get(`/recovery-packages/${packageId}`);
        setSelectedPackage(response.data);
        setPackages([response.data]);
      } else {
        // Load all packages for review
        const pendingResponse = await api.get("/recovery-packages/pending-review");
        const allResponse = await api.get("/recovery-packages/all");
        setPackages(allResponse.data);
        
        if (pendingResponse.data.length > 0) {
          setSelectedPackage(pendingResponse.data[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load recovery packages");
    } finally {
      setLoading(false);
    }
  }, [api, packageId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReview = async () => {
    if (!reviewAction) {
      toast.error("Please select an action");
      return;
    }
    
    if ((reviewAction === "reject" || reviewAction === "request_revision") && !reviewComments) {
      toast.error("Please provide feedback for rejection or revision request");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/recovery-packages/${selectedPackage.id}/review`, {
        action: reviewAction,
        comments: reviewComments,
      });
      
      toast.success(
        reviewAction === "approve" ? "Package approved!" :
        reviewAction === "reject" ? "Package rejected" :
        "Revision requested"
      );
      
      setShowReviewDialog(false);
      setReviewComments("");
      setReviewAction("");
      fetchData();
    } catch (error) {
      console.error("Failed to submit review:", error);
      toast.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const pendingPackages = packages.filter(p => ["submitted", "under_review"].includes(p.status));
  const approvedPackages = packages.filter(p => p.status === "approved");
  const otherPackages = packages.filter(p => !["submitted", "under_review", "approved"].includes(p.status));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recovery-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-100 text-emerald-700">DO Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 8.4</Badge>
            <Badge className="bg-sky-100 text-sky-700">BCM Manager</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Recovery Package Overview</h1>
          <p className="text-slate-600 mt-1">
            View all recovery packages across departments. Approvals are handled by designated plan approvers.
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={pendingPackages.length > 0 ? "border-amber-200 bg-amber-50" : ""}>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Pending Review</p>
            <p className="text-2xl font-bold text-amber-600">{pendingPackages.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Approved</p>
            <p className="text-2xl font-bold text-emerald-600">{approvedPackages.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Draft/Revision</p>
            <p className="text-2xl font-bold text-slate-600">{otherPackages.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Total Packages</p>
            <p className="text-2xl font-bold text-slate-900">{packages.length}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Package List */}
        <Card className="col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Packages</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start rounded-none border-b">
                <TabsTrigger value="pending" className="relative">
                  Pending
                  {pendingPackages.length > 0 && (
                    <Badge className="ml-1 bg-amber-500 text-white text-xs">{pendingPackages.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="approved">Approved</TabsTrigger>
                <TabsTrigger value="other">Other</TabsTrigger>
              </TabsList>
              
              <TabsContent value="pending" className="mt-0">
                <div className="divide-y max-h-[500px] overflow-y-auto">
                  {pendingPackages.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">No pending packages</div>
                  ) : (
                    pendingPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`p-3 cursor-pointer hover:bg-slate-50 ${selectedPackage?.id === pkg.id ? "bg-sky-50 border-l-4 border-sky-700" : ""}`}
                        onClick={() => setSelectedPackage(pkg)}
                      >
                        <p className="font-medium text-sm">{pkg.process_name}</p>
                        <p className="text-xs text-slate-500">{pkg.process_owner_name}</p>
                        <Badge className="mt-1 text-xs" variant="outline">
                          {new Date(pkg.submitted_at).toLocaleDateString()}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="approved" className="mt-0">
                <div className="divide-y max-h-[500px] overflow-y-auto">
                  {approvedPackages.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">No approved packages</div>
                  ) : (
                    approvedPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`p-3 cursor-pointer hover:bg-slate-50 ${selectedPackage?.id === pkg.id ? "bg-sky-50 border-l-4 border-sky-700" : ""}`}
                        onClick={() => setSelectedPackage(pkg)}
                      >
                        <p className="font-medium text-sm">{pkg.process_name}</p>
                        <p className="text-xs text-slate-500">{pkg.process_owner_name}</p>
                        <Badge className="mt-1 text-xs bg-emerald-100 text-emerald-700">Approved</Badge>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="other" className="mt-0">
                <div className="divide-y max-h-[500px] overflow-y-auto">
                  {otherPackages.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">No other packages</div>
                  ) : (
                    otherPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`p-3 cursor-pointer hover:bg-slate-50 ${selectedPackage?.id === pkg.id ? "bg-sky-50 border-l-4 border-sky-700" : ""}`}
                        onClick={() => setSelectedPackage(pkg)}
                      >
                        <p className="font-medium text-sm">{pkg.process_name}</p>
                        <p className="text-xs text-slate-500">{pkg.process_owner_name}</p>
                        <Badge className={`mt-1 text-xs ${STATUS_CONFIG[pkg.status]?.color || ""}`}>
                          {STATUS_CONFIG[pkg.status]?.label || pkg.status}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Package Details */}
        <Card className="col-span-2">
          {!selectedPackage ? (
            <CardContent className="pt-12 text-center">
              <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Select a package to review</p>
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{selectedPackage.process_name}</CardTitle>
                    <CardDescription>
                      {selectedPackage.department} • Owner: {selectedPackage.process_owner_name}
                    </CardDescription>
                  </div>
                  <Badge className={STATUS_CONFIG[selectedPackage.status]?.color || ""}>
                    {STATUS_CONFIG[selectedPackage.status]?.label || selectedPackage.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4 max-h-[500px] overflow-y-auto">
                {/* BIA Summary */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-600">Target RTO</p>
                    <p className="text-lg font-bold text-red-700">{selectedPackage.bia_summary?.rto_hours}h</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600">RPO</p>
                    <p className="text-lg font-bold text-amber-700">{selectedPackage.bia_summary?.rpo_hours}h</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600">Planned RTO</p>
                    <p className={`text-lg font-bold ${
                      selectedPackage.recovery_strategy?.planned_rto_hours > selectedPackage.bia_summary?.rto_hours
                        ? "text-red-600"
                        : "text-emerald-600"
                    }`}>
                      {selectedPackage.recovery_strategy?.planned_rto_hours || "N/A"}h
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-600">MBCO</p>
                    <p className="text-lg font-bold text-emerald-700">{selectedPackage.bia_summary?.mbco_percent}%</p>
                  </div>
                </div>

                {/* Strategy */}
                {selectedPackage.recovery_strategy?.strategy_type && (
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-slate-600" />
                      <p className="font-medium text-sm">Recovery Strategy</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={STRATEGY_CONFIG[selectedPackage.recovery_strategy.strategy_type]?.color || ""}>
                        {STRATEGY_CONFIG[selectedPackage.recovery_strategy.strategy_type]?.label}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        ({STRATEGY_CONFIG[selectedPackage.recovery_strategy.strategy_type]?.rto})
                      </span>
                    </div>
                    {selectedPackage.recovery_strategy.justification && (
                      <p className="text-sm text-slate-600 mt-2">{selectedPackage.recovery_strategy.justification}</p>
                    )}
                  </div>
                )}

                {/* Business Recovery */}
                {selectedPackage.business_recovery?.manual_workarounds && (
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-slate-600" />
                      <p className="font-medium text-sm">Business Recovery</p>
                    </div>
                    <p className="text-sm text-slate-600">{selectedPackage.business_recovery.manual_workarounds}</p>
                    <div className="flex gap-4 mt-2 text-xs text-slate-500">
                      <span>Duration: {selectedPackage.business_recovery.workaround_duration_hours}h</span>
                      <span>Min Staff: {selectedPackage.business_recovery.minimum_staff_required}</span>
                    </div>
                  </div>
                )}

                {/* Application Recovery — Always renders in template table format */}
                {(selectedPackage.application_recovery?.recovery_tasks?.length > 0 || selectedPackage.application_recovery?.steps?.length > 0) && (
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Server className="w-4 h-4 text-slate-600" />
                      <p className="font-medium text-sm">Application Recovery Tasks</p>
                    </div>
                    {(() => {
                      const tasks = selectedPackage.application_recovery.recovery_tasks?.length > 0
                        ? selectedPackage.application_recovery.recovery_tasks
                        : (selectedPackage.application_recovery.steps || []).map((step, idx) => ({
                            task_id: `T${String(idx + 1).padStart(3, '0')}`,
                            actual_task: step.action,
                            task_owner: step.responsible || "—",
                            predecessor_task_id: idx > 0 ? `T${String(idx).padStart(3, '0')}` : null,
                            estimated_duration: `${step.duration_mins} mins`,
                            estimated_duration_mins: step.duration_mins,
                            start_time: null,
                            completion_time: null,
                            notes: step.notes || "",
                          }));
                      return (
                        <div className="border rounded overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-800">
                                <TableHead className="text-white text-xs font-semibold w-14">Task ID</TableHead>
                                <TableHead className="text-white text-xs font-semibold">Actual Task</TableHead>
                                <TableHead className="text-white text-xs font-semibold w-24">Task Owner</TableHead>
                                <TableHead className="text-white text-xs font-semibold w-20">Predecessor</TableHead>
                                <TableHead className="text-white text-xs font-semibold w-20">Est. Duration</TableHead>
                                <TableHead className="text-white text-xs font-semibold w-20">Start Time</TableHead>
                                <TableHead className="text-white text-xs font-semibold w-24">Completion</TableHead>
                                <TableHead className="text-white text-xs font-semibold w-24">Notes</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {tasks.map((task, idx) => (
                                <TableRow key={idx} className="text-xs" data-testid={`pkg-review-task-${idx}`}>
                                  <TableCell className="font-mono font-semibold text-purple-700">{task.task_id}</TableCell>
                                  <TableCell className="font-medium">{task.actual_task}</TableCell>
                                  <TableCell>{task.task_owner}</TableCell>
                                  <TableCell className="font-mono text-slate-500">{task.predecessor_task_id || "—"}</TableCell>
                                  <TableCell>{task.estimated_duration}</TableCell>
                                  <TableCell className="text-slate-400 italic">{task.start_time || "—"}</TableCell>
                                  <TableCell className="text-slate-400 italic">{task.completion_time || "—"}</TableCell>
                                  <TableCell className="text-slate-500 truncate max-w-[80px]">{task.notes || "—"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Dependencies */}
                {(selectedPackage.dependency_handoffs?.upstream?.length > 0 || 
                  selectedPackage.dependency_handoffs?.downstream?.length > 0) && (
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <GitBranch className="w-4 h-4 text-slate-600" />
                      <p className="font-medium text-sm">Dependencies</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Upstream ({selectedPackage.dependency_handoffs?.upstream?.length || 0})</p>
                        {selectedPackage.dependency_handoffs?.upstream?.map((dep, idx) => (
                          <Badge key={idx} variant="outline" className="mr-1 mb-1 text-xs">{dep.dependency_name}</Badge>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Downstream ({selectedPackage.dependency_handoffs?.downstream?.length || 0})</p>
                        {selectedPackage.dependency_handoffs?.downstream?.map((dep, idx) => (
                          <Badge key={idx} variant="outline" className="mr-1 mb-1 text-xs">{dep.dependency_name}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Infrastructure Support */}
                {(selectedPackage.infrastructure_support?.it_team_required || 
                  selectedPackage.infrastructure_support?.facility_team_required) && (
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-slate-600" />
                      <p className="font-medium text-sm">Infrastructure Support Required</p>
                    </div>
                    <div className="flex gap-2">
                      {selectedPackage.infrastructure_support?.it_team_required && (
                        <Badge variant="outline">IT Team</Badge>
                      )}
                      {selectedPackage.infrastructure_support?.facility_team_required && (
                        <Badge variant="outline">Facility Team</Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Approver Info & View-Only Notice */}
                {selectedPackage.approver?.name && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-xs text-purple-600 mb-1">Designated Approver</p>
                    <p className="font-medium text-sm text-purple-800">{selectedPackage.approver.name} ({selectedPackage.approver.email})</p>
                  </div>
                )}
                {["submitted", "under_review"].includes(selectedPackage.status) && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                    <p className="text-sm text-slate-600">
                      {selectedPackage.approver?.name
                        ? `This package is pending review by ${selectedPackage.approver.name}`
                        : "This package is pending review by its designated approver"}
                    </p>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === "approve" && "Approve Recovery Package"}
              {reviewAction === "request_revision" && "Request Revision"}
              {reviewAction === "reject" && "Reject Recovery Package"}
            </DialogTitle>
            <DialogDescription>
              {reviewAction === "approve" && "This will approve the recovery package and notify the process owner."}
              {reviewAction === "request_revision" && "The process owner will be notified to make revisions."}
              {reviewAction === "reject" && "This will reject the recovery package. Please provide a reason."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Comments {reviewAction !== "approve" && <span className="text-red-500">*</span>}</Label>
            <Textarea
              value={reviewComments}
              onChange={(e) => setReviewComments(e.target.value)}
              placeholder={
                reviewAction === "approve" ? "Optional comments..." :
                "Provide feedback for the process owner..."
              }
              rows={4}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              disabled={submitting}
              className={
                reviewAction === "approve" ? "bg-emerald-600 hover:bg-emerald-700" :
                reviewAction === "reject" ? "bg-red-600 hover:bg-red-700" :
                "bg-sky-800 hover:bg-sky-800"
              }
            >
              {submitting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {reviewAction === "approve" && "Approve"}
              {reviewAction === "request_revision" && "Request Revision"}
              {reviewAction === "reject" && "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
